<script>
  // One contract per browser: blocks the tab while another tab is active with a
  // different contract or user, or after a logout in another tab.
  import { Button, Flex, Modal, Text } from "@gl/design-system";
  import { _, user, contract } from "../stores/index";
  import sessionCookie from "../services/sessionCookie";
  import { reloadIntoContract } from "../services/contract";
  import { blockPageBehind } from "../utils/blockPageBehind";

  // Modal puts its `class` on the layer it renders into <body>
  const LAYER_CLASS = "session-conflict";

  const { conflict } = sessionCookie;

  let busy = false;
  let active = false;

  $: active = $conflict != null;
  $: otherContract = $user?.contracts?.find(
    (candidate) => candidate.id === $conflict?.contractId,
  );
  $: otherName = otherContract?.name ?? $conflict?.contractId;
  $: ownName = $contract?.name ?? $contract?.id;

  async function run(action) {
    busy = true;
    try {
      await action();
    } finally {
      busy = false;
    }
  }

  const switchHere = () => run(() => reloadIntoContract(otherContract));

  const keepHere = () => run(() => sessionCookie.reclaim());

  const reload = () => window.location.reload();

  // × and Escape: "continue here" is the cancel of a contract conflict (UX,
  // #869fh0v3k); after a logout or another user only a reload gets out
  async function dismiss() {
    try {
      if (busy) return;
      if ($conflict?.reason !== "contract") return reload();

      await keepHere();
    } finally {
      // the Modal has closed itself, it stays open while the conflict remains
      active = $conflict != null;
    }
  }
</script>

<Modal bind:active class={LAYER_CLASS} boxed on:close={dismiss}>
  {#if $conflict}
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-conflict-title"
      aria-describedby="session-conflict-text"
      use:blockPageBehind={`.${LAYER_CLASS}`}
    >
      <div class="gl-title mb-4" id="session-conflict-title">
        {$_(`session.conflict.${$conflict.reason}.title`)}
      </div>
      <div id="session-conflict-text">
        <Text>{$_(`session.conflict.${$conflict.reason}.text`)}</Text>
      </div>
      <Flex class="mt-6 justify-content-start">
        {#if $conflict.reason === "contract"}
          {#if otherContract}
            <Button class="mr-3" loading={busy} on:click={switchHere}>
              {$_("session.conflict.contract.switch", { values: { other: otherName } })}
            </Button>
          {/if}
          <Button outlined loading={busy} on:click={keepHere}>
            {$_("session.conflict.contract.keep", { values: { own: ownName } })}
          </Button>
        {:else}
          <Button on:click={reload}>{$_("session.conflict.reload")}</Button>
        {/if}
      </Flex>
    </div>
  {/if}
</Modal>
